import React, { useEffect, useState } from 'react';
import { useApiClient } from '../api/client';

export type UserSearchResult = { id: string; email: string; firstName?: string | null; lastName?: string | null };

export const UserTypeahead: React.FC<{
  onSelect: (user: UserSearchResult) => void;
  placeholder?: string;
  communityId?: string;
  ariaLabel?: string;
}> = ({ onSelect, placeholder, communityId, ariaLabel }) => {
  const { request } = useApiClient();
  const [q, setQ] = useState('');
  const [items, setItems] = useState<UserSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const t = setTimeout(async () => {
      if (!q.trim()) { setItems([]); return; }
      setLoading(true);
      try {
        const url = communityId
          ? `/me/searchInCommunity?communityId=${encodeURIComponent(communityId)}&q=${encodeURIComponent(q)}`
          : `/me/search?q=${encodeURIComponent(q)}`;
        const res = await request<UserSearchResult[]>(url);
        if (active) { setItems(res); setOpen(true); }
      } catch { if (active) { setItems([]); setOpen(false); } }
      finally { if (active) setLoading(false); }
    }, 200);
    return () => { active = false; clearTimeout(t); };
  }, [communityId, q, request]);

  return (
    <div className="relative">
      <input
        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/30"
        placeholder={placeholder || 'Search user by name or email'}
        aria-label={ariaLabel}
        value={q}
        onChange={(e) => { setQ(e.target.value); setItems([]); setOpen(false); setLoading(false); }}
        onFocus={() => { if (items.length) setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
      />
      {open && items.length > 0 && (
        <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
          <ul className="max-h-56 overflow-auto py-1 text-sm">
            {items.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-3 py-2 hover:bg-gray-50"
                  onClick={() => { onSelect(u); setQ(''); setItems([]); setOpen(false); }}
                >
                  <span>{u.email}</span>
                  <span className="text-gray-500">{[u.firstName, u.lastName].filter(Boolean).join(' ')}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {loading && <div className="absolute right-2 top-2 text-xs text-gray-400">…</div>}
    </div>
  );
};
